import React, { useState } from 'react';
import { Box, Typography, Divider, Paper, Button,  Grid2, IconButton } from '@mui/material';
import { CounselingDetailsFormValues, CounselingType, CounselingFeedback } from './counselingDetailsFormHelper';
import styles from './CounselingDetails.module.css';
import { ReactComponent as Edit } from '../../../../../assets/SVG/Edit.svg';
import { useNavigate } from 'react-router-dom';
import { usePatientContext } from '../../../../../components/SubPatinetProvider';
import { useTranslation } from 'react-i18next';
import { convertDateToDDMMYYYY } from '../../../../../utils/commonUtils';
import { FileViewerModal, UploadedImageType, extractFilename, ViewFilesButton } from '../../../../../components/common/ImageUploadModal';
import TruncatedText from '../../../../../utils/TruncatedText';

type ViewData = {
  session_details: CounselingDetailsFormValues['session_details'];
  counseling_types: CounselingType[];
  feedback: CounselingFeedback[];
};

interface Props {
  data: ViewData;
  onBack?: () => void;
}

const CounselingDetailsViewMode: React.FC<Props> = ({ data, onBack }) => {
  const navigate = useNavigate();
  const {patientId} = usePatientContext();
  const {t:tBilling} = useTranslation('billing_config');
  const {t:tGeneral} = useTranslation('general');
  const [viewingTypeIdx, setViewingTypeIdx] = useState<number | null>(null);

  // Type for report_file array items
  type ReportFileItem = { image?: string; id?: number; file_name?: string; file_size?: number; file_extension?: string };

  // Process images per counseling type for display (from report_file array)
  const imagesPerType: Record<number, UploadedImageType[]> = React.useMemo(() => {
    const result: Record<number, UploadedImageType[]> = {};
    (data.counseling_types ?? []).forEach((ct, idx) => {
      // Check if report_file is an array of images
      const reportFiles: ReportFileItem[] = Array.isArray(ct.report_file) ? ct.report_file : [];
      if (reportFiles.length > 0) {
        result[idx] = reportFiles.map((img: ReportFileItem, imgIndex: number) => {
          const imageUrl = img.image || '';
          const backendFileName = img.file_name;
          const extractedName = extractFilename(imageUrl || '');
          const cleanFileName = backendFileName || extractedName || `Image_${imgIndex + 1}`;
          
          return {
            name: cleanFileName,
            image: imageUrl,
            size: img.file_size ? (img.file_size / (1024 * 1024)).toFixed(2) : undefined,
            sizeBytes: img.file_size,
            file_extension: img.file_extension,
            isExisting: true,
            id: img.id,
          };
        });
      } else {
        result[idx] = [];
      }
    });
    return result;
  }, [data.counseling_types]);
  

  const toLabel = (value: unknown, fallback = '-') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (typeof value === 'object') {
      const maybeName = (value as { name?: unknown }).name;
      if (typeof maybeName === 'string' || typeof maybeName === 'number' || typeof maybeName === 'boolean') {
        return String(maybeName);
      }
    }
    return fallback;
  };

  // Helper to check if a value is meaningful (not null, undefined, empty, or '-')
  const hasValue = (value: unknown): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim() !== '' && value !== '-';
    if (typeof value === 'number' || typeof value === 'boolean') return true;
    if (typeof value === 'object') {
      const maybeName = (value as { name?: unknown }).name;
      if (typeof maybeName === 'string') return maybeName.trim() !== '' && maybeName !== '-';
      if (typeof maybeName === 'number' || typeof maybeName === 'boolean') return true;
    }
    return false;
  };
  const counselingDetails = {
    counselingDate: convertDateToDDMMYYYY(data?.session_details?.counseling_date ?? '-') ,
    counselingTime: data?.session_details?.counseling_time ?? '-',
    counselorName: data?.session_details?.counselor_name ?? '-',
    nextVisitDate: data?.session_details?.next_visit_date ? convertDateToDDMMYYYY(data.session_details.next_visit_date) : '-' ,
    counselingStage: data?.session_details?.counseling_stage_label ?? '-',
    counselingRemark: data?.session_details?.counseling_remark ?? '-',
    sourceOfReference: data?.session_details?.source_of_reference_label ?? '-',
    referenceDoctor: '-',
    treatmentAdvice: String(data?.session_details?.treatment_advice ?? '-') ,
    treatmentDoctor: String(data?.session_details?.treatment_doctor_label?? '-') ,
    adviceDate: convertDateToDDMMYYYY(data?.session_details?.created_on ?? '-') ,
    isFreezed: data?.session_details?.is_freezed,
  };

  const counselingType = (data?.counseling_types ?? []).map(ct => ({
    type: ct.type_label ?? ct.type?.name ?? '-',
    date: convertDateToDDMMYYYY(ct.date ?? '-') ,
    remark: ct.remark ?? '-',
  }));

  const feedbackRows = (data.feedback ?? []).map((f) => {
    const fRec = f as unknown as Record<string, unknown>;
    const prValue = fRec.patient_response_label ?? fRec.patient_response;
    return {
      date: f.feedback_date ?? '-',
      response: toLabel(prValue, '-'),
      remarks: f.patient_remarks ?? '-',
      feedback1: f.feedback ?? '-',
      feedback2: '-',
    };
  });
  return (
    <Box data-testid="counseling-details-view-mode" sx={{ px: 3, py: 2 }}>
      <Box data-testid="counseling-details-view-header" sx={{ display: 'flex', justifyContent: !counselingDetails.isFreezed ? 'space-between' : 'flex-start', mb: 2 }}>
        <Typography
          variant="h6"
          className={styles.h6Title}
          sx={{ mb: 3 }}
        >
          {tBilling('counseling_details.counseling_details')}
        </Typography>

        {!counselingDetails.isFreezed &&(
          <IconButton data-testid="counseling-details-edit-btn" onClick={onBack}>
            <Edit  />
          </IconButton>
        )}
      </Box>
      {(() => {
        const headerFields: Array<{ label: string; value: unknown; isTruncated?: boolean }> = [
          { label: tBilling('counseling_details.counseling_date'), value: counselingDetails.counselingDate },
          { label: tBilling('counseling_details.counseling_time'), value: counselingDetails.counselingTime },
          { label: tBilling('counseling_details.counselor_name'), value: counselingDetails.counselorName },
          { label: tBilling('counseling_details.next_visit_date_by_counsellor'), value: counselingDetails.nextVisitDate },
          { label: tBilling('counseling_details.counseling_stage'), value: counselingDetails.counselingStage },
          { label: tBilling('counseling_details.counseling_remark'), value: counselingDetails.counselingRemark, isTruncated: true },
          { label: tBilling('counseling_details.source_of_reference'), value: counselingDetails.sourceOfReference },
          { label: tBilling('counseling_details.reference_doctor'), value: counselingDetails.referenceDoctor },
          { label: tBilling('counseling_details.treatment_advice'), value: counselingDetails.treatmentAdvice },
          { label: tBilling('counseling_details.treatment_doctor'), value: counselingDetails.treatmentDoctor },
          { label: tBilling('counseling_details.advice_date'), value: counselingDetails.adviceDate },
        ];
        return (
          <Grid2 container spacing={2}>
            {headerFields.filter(f => hasValue(f.value)).map((f, i) => (
              <Grid2 key={i} size={{ xl: 3, lg: 4, md: 6, xs: 12 }}>
                <Typography className={styles.key}>{f.label}</Typography>
                <Typography className={styles.value}>
                  {f.isTruncated ? (
                    <TruncatedText text={toLabel(f.value, '-')} title={f.label} iconPosition="left" />
                  ) : (
                    toLabel(f.value, '-')
                  )}
                </Typography>
              </Grid2>
            ))}
          </Grid2>
        );
      })()}
      <Box><Divider sx={{ my: 3, borderColor: '#afb0b1ff' }} /></Box>
      {counselingType.length > 0 && (
        <>
          <Typography
            variant="h6"
            className={styles.h6Title ?? '-'}
            sx={{ mb: 1 }}
          >
            {tBilling('counseling_details.counseling_type')}
          </Typography>
          {counselingType.map((type, idx) => {
            const typeImages = imagesPerType[idx] || [];
            return (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 4, mb: 3 }} key={idx}>
                {(() => {
                  const fields: Array<{ label: string; node: React.ReactNode; isTruncated?: boolean }> = [
                    { label: tBilling('counseling_details.type'), node: type.type ?? '-' },
                    { label: tBilling('counseling_details.date'), node: type.date ?? '-' },
                    { label: tBilling('counseling_details.remark'), node: type.remark ?? '-', isTruncated: true },
                  ];
                  return (
                    <Grid2 container spacing={2} alignItems="center">
                      {fields.filter(f => hasValue(f.node)).map((f, i) => (
                        <Grid2 key={i} size={{ xl: 3, lg: 4, md: 6, xs: 12 }}>
                          <Typography className={styles.key}>{f.label}</Typography>
                          <Typography className={styles.value}>
                            {f.isTruncated ? (
                              <TruncatedText text={toLabel(f.node, '-')} title={f.label} iconPosition="left" />
                            ) : (
                              toLabel(f.node, '-')
                            )}
                          </Typography>
                        </Grid2>
                      ))}
                      {/* Uploaded files per counseling type */}
                      {typeImages.length > 0 && (
                        <Grid2 size={{ xl: 3, lg: 4, md: 6, xs: 12 }}>
                          <Typography className={styles.key}>{tBilling('counseling_details.uploaded_report')}</Typography>
                          <Box sx={{ mt: 1 }}>
                            <ViewFilesButton
                              fileCount={typeImages.length}
                              onClick={() => setViewingTypeIdx(idx)}
                              tooltip={tGeneral('View uploaded images')}
                            />
                          </Box>
                        </Grid2>
                      )}
                    </Grid2>
                  );
                })()}
              </Paper>
            );
          })}
        </>
      )}

      <Typography
        variant="h6"
        className={styles.h6Title}
        sx={{ mb: 2 }}
      >
        {tBilling('counseling_details.feedback_details')}
      </Typography>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 4, mb: 3 }}>
        {feedbackRows.map((row, idx) => {
          const feedbackFields = [
            { label: tBilling('counseling_details.date'), value: row.date, isTruncated: false },
            { label: tBilling('counseling_details.patient_response'), value: row.response, isTruncated: false },
            { label: tBilling('counseling_details.patient_remarks'), value: row.remarks, isTruncated: true },
            { label: tBilling('counseling_details.feedback'), value: row.feedback1, isTruncated: true },
          ].filter(f => hasValue(f.value));
          if (feedbackFields.length === 0) return null;
          return (
            <Grid2 container spacing={2} key={idx} sx={{ mb: 1 }}>
              {feedbackFields.map((f, i) => (
                <Grid2 key={i} size={{ xl: 3, lg: 4, md: 6, xs: 12 }}>
                  <Typography className={styles.key}>{f.label}</Typography>
                  <Typography className={styles.value}>
                    {f.isTruncated ? (
                      <TruncatedText text={toLabel(f.value, '-')} title={f.label} iconPosition="left" />
                    ) : (
                      toLabel(f.value, '-')
                    )}
                  </Typography>
                </Grid2>
              ))}
            </Grid2>
          );
        })}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
        <Button
          variant="outlined"
          sx={{ minWidth: 180, fontWeight: 600, borderRadius: 2, fontSize: 18 }}
          onClick={() => {
            navigate(`/admin/billing/package/patient-package/${patientId}`, { state: { refetch: true, ts: Date.now() } });
          }}
        >
          {tBilling('counseling_details.back_to_listing')}
        </Button>
      </Box>

      {/* File Viewer Modal - Per counseling type */}
      <FileViewerModal
        open={viewingTypeIdx !== null}
        onClose={() => setViewingTypeIdx(null)}
        files={viewingTypeIdx !== null ? (imagesPerType[viewingTypeIdx] || []) : []}
        title={tGeneral('Uploaded Files')}
      />
    </Box>
  );
};

export default CounselingDetailsViewMode;
